import React from 'react';
import { JcBox } from './JcBox';
import { JcCommonProps, JcUtils } from '../utils/JcUtils';

export interface JcResponsiveSplitProps extends JcCommonProps {
    /** Percentage of the width the primary (first) child occupies when laid out
     *  side-by-side, e.g. 40. The secondary child fills the rest. */
    readonly primaryPercent?: number;
    /** Container width (px) below which the panes stack. Defaults to 650. */
    readonly breakpoint?: number;
    /** Add the standard gap between the two panes (mirrors JcBox `spacing`). */
    readonly spacing?: boolean;
    /** Exactly two children: [primary, secondary]. Primary is left/top. */
    readonly children: readonly [React.ReactNode, React.ReactNode];
}

type JcCssVars = React.CSSProperties & Record<`--${string}`, string | number>;

/**
 * Two unalike panes (e.g. an image next to a details column, or two different
 * form sections) side-by-side above a breakpoint, stacked below it. Unlike
 * `JcResponsiveGrid`, the panes don't need matching shapes or equal width —
 * `primaryPercent` lets one lead.
 */
export const JcResponsiveSplit = (
    props: JcResponsiveSplitProps,
): React.ReactElement => {
    const { primaryPercent = 50, spacing } = props;
    const breakpoint = props.breakpoint ?? 650;
    const [primary, secondary] = props.children;
    const commonProps = JcUtils.commonProps(props, ['jc-responsive-split']);

    const style: JcCssVars = {
        ...props.style,
        '--jc-responsive-split-breakpoint': `${breakpoint}px`,
        '--jc-responsive-split-primary-grow': primaryPercent,
    };

    return (
        <JcBox {...commonProps} style={style} spacing={spacing}>
            {primary}
            {secondary}
        </JcBox>
    );
};
